import { useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PageContainer from '../Components/PageContainer';
import useGetProcessedTimesheetDetails from '../hooks/useGetProcessedTimesheetDetails';
import ProcessedTimesheetDetailsTable from '../Components/ProcessedTimesheets/ProcessedTimesheetDetailsTable';
import { isPtsReadOnly } from '../Configurations/processedTimesheetsConfig';
import {
    ProcessedTimesheetContextProvider,
    useProcessedTimesheetContext,
} from '../Contexts/ProcessedTimesheetContext';
import useAdminAuthContext from '../hooks/useAdminAuthContext';

const useInitialSortQueryParams = () => {
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const field = params.get('sortField') || 'date';
    const sort = params.get('sortDirection') || 'asc';
    return [{ field, sort }];
};

const ProcessedTimesheetDetailsTablePage = () => {
    const { id: processedTimesheetId } = useParams();
    const { data: timesheetDetailsData, isLoading } = useGetProcessedTimesheetDetails({
        processedTimesheetId,
    });
    const initialSortModel = useInitialSortQueryParams();
    const { hasRecentChanges } = useProcessedTimesheetContext();
    const { ltoeApiTimesheetEnableEditApprovedTimesheet } = useFlags();
    const readOnly =
        isPtsReadOnly(timesheetDetailsData?.status) ||
        hasRecentChanges ||
        !ltoeApiTimesheetEnableEditApprovedTimesheet;
    const { isTimesheetSystemAdminUser } = useAdminAuthContext();

    const locations = useMemo(() => {
        if (!timesheetDetailsData?.timeEntries?.entriesByLocation) return [];

        return timesheetDetailsData.timeEntries.entriesByLocation.map((locationTimeEntry) => ({
            locationName: locationTimeEntry.locationName,
            locationId: locationTimeEntry.locationId,
        }));
    }, [timesheetDetailsData?.timeEntries?.entriesByLocation]);

    return (
        <PageContainer>
            <Box sx={{ paddingRight: '30px' }}>
                <ProcessedTimesheetDetailsTable
                    initialSortModel={initialSortModel}
                    data={timesheetDetailsData?.processedTimesheetEntries || []}
                    isLoading={isLoading}
                    availableRates={timesheetDetailsData?.availableRates || []}
                    hideOpenButton
                    readOnly={readOnly || !isTimesheetSystemAdminUser}
                    locations={locations}
                    splitByWorkLocation={timesheetDetailsData?.splitByWorkLocation}
                />
            </Box>
        </PageContainer>
    );
};

const ProcessedTimesheetDetailsTablePageWithContext = () => (
    <ProcessedTimesheetContextProvider>
        <ProcessedTimesheetDetailsTablePage />
    </ProcessedTimesheetContextProvider>
);

export default ProcessedTimesheetDetailsTablePageWithContext;
